import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UseQueryResult } from '@tanstack/react-query';

import { DetailedTitle } from '@models/Title';

import { RootState } from '..';

import { SetTitleQueryResultPayload } from './types';

type GlobalVarsState = {
  disableCache: boolean;
  fetchTitleStatusMap: Record<
    string,
    Pick<UseQueryResult<DetailedTitle | undefined>, 'isError' | 'isFetching'>
  >;
};

const initialState: GlobalVarsState = {
  disableCache: false,
  fetchTitleStatusMap: {},
};

// Base selectors
const selectFetchTitleStatusMap = (state: RootState) => state.globalVars.fetchTitleStatusMap;

// Property selectors
export const getDisableCache = (state: RootState) => state.globalVars.disableCache;

export const shouldNudgeCountrySelector = createSelector(
  [selectFetchTitleStatusMap],
  fetchTitleStatusMap => {
    const queries = Object.values(fetchTitleStatusMap);
    if (queries.length === 0) return false;
    if (queries.some(({ isFetching }) => isFetching)) return false;

    const failedQueries = queries.filter(({ isError }) => isError);
    if (failedQueries.length >= 5) return true;

    return failedQueries.length / queries.length >= 1 / 3;
  }
);

// SLICE
export const globalVarsSlice = createSlice({
  name: 'globalVars',
  initialState,
  reducers: {
    setDisableCache: (state, { payload: disableCache }: PayloadAction<boolean>) => {
      state.disableCache = disableCache;
    },

    setFetchTitleStatus: (
      state,
      { payload: { masterId, isError, isFetching } }: PayloadAction<SetTitleQueryResultPayload>
    ) => {
      state.fetchTitleStatusMap[masterId] = { isError, isFetching };
    },

    removeFetchTitleStatus: (state, { payload: masterId }: PayloadAction<string>) => {
      delete state.fetchTitleStatusMap[masterId];
    },

    clearFetchTitleStatuses: state => {
      state.fetchTitleStatusMap = {};
    },
  },
});

export const { actions: globalVarsActions } = globalVarsSlice;

export default globalVarsSlice.reducer;
